import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  from,
  split,
  HttpLink
} from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'

import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { pathOr } from 'ramda'
import { message } from 'antd'
import { createUploadLink } from 'apollo-upload-client'
import config from 'constants/config'
import { getFromStorage } from 'utils/localStorage'
import { getFromSessionStorage } from 'utils/sessionStorage'
import { mapMaxIndexToValue } from 'utils/helpers'
import { getUnixTimestampFormat } from 'utils/helpers/dateHelper'
import { timeDiff } from 'apollo/variables'

import { getOperationAST } from 'graphql'
import { refreshSession, signOut } from 'apollo/auth'
import logger from 'utils/logger'
import { getCompactTagVersion } from 'utils/renderHelpers'

import fragmentResolutions from 'apollo/__generated__/fragmentResolutions.json'
import { typePolicies } from './cacheTypePolicy'
import {
  configureTicketedCable,
  disconnectTicketedCable,
  ticketedActionCableLink
} from './ticketedCable'

export const getToken = getFromStorage('token')
export const getRefreshToken = getFromStorage('refresh')

// Requested resource not found
const handleNotFound = error => {
  message.error(error.message)
  logger.logError(error.message, {}, error)
}

// There was an error updating a resource
const handleNotUpdated = error => {
  message.error(error.message)
  logger.logError(error.message, {}, error)
}

// Unprocessed and unhandled server error
const handleServerError = error => {
  message.error(error.message)
  logger.logError(error.message, {}, error)
}

// Access token is wrong or missing
const handleUnauthenticated = () => {
  if (window.location.pathname !== '/sso') {
    if (getToken()) refreshSession()
    else signOut()
  }
}

// Attempted to access the inaccessible
const handleUnauthorized = error => {
  message.error(error.message)
  logger.logError(error.message, {}, error)
}

const handleUnknown = error => {
  message.error(error.message)
  logger.logError(error.message, {}, error)
}

const handlePbmError = (error, code, { showPbmErrorMessage } = {}) => {
  // opt-in per operation (context.showPbmErrorMessage) for user-facing validation, e.g. duplicate plan rule
  if (showPbmErrorMessage) return message.error(error?.message)
  if (error?.message?.includes('INVALID_ARGUMENT')) {
    return
  }
  if (code === 'UNAUTHORIZED') {
    return message.error(error?.message)
  }
  message.error('Something went wrong. Unknown PBM server error')
}

const handleGraphQLError = (error, context) => {
  const code = pathOr(null, ['extensions', 'code'], error)
  const serviceName = pathOr(null, ['extensions', 'serviceName'], error)

  if (serviceName === 'pbm') return handlePbmError(error, code, context)
  // logger.logError('handleGraphQLError', { code }, error)

  switch (code) {
    case 'NOT_FOUND': {
      handleNotFound(error)
      break
    }
    case 'NOT_UPDATED': {
      handleNotUpdated(error)
      break
    }
    case 'SERVER_ERROR': {
      handleServerError(error)
      break
    }
    case 'UNAUTHENTICATED': {
      handleUnauthenticated()
      break
    }
    case 'UNAUTHORIZED': {
      handleUnauthorized(error)
      break
    }
    default: {
      handleUnknown(error)
      break
    }
  }
}

const httpLink = new HttpLink({
  uri: config.API_URL
})

const tagVersion = getCompactTagVersion(__TAG_VERSION__)
const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = getToken()
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      'Rightway-Consumer-Version': `AdvocateWeb/${tagVersion}`,
      Authorization: token ? `Bearer ${token}` : ''
    }
  }
})

const auxillaryHeadersLink = setContext(
  ({ operationName }, { headers, memberID, subsection }) => ({
    headers: {
      ...headers,
      'X-Apollo-Operation-Name': operationName,
      ...(memberID
        ? {
            'Employee-Id': memberID
          }
        : {}),
      ...(subsection ? { subsection } : {})
    }
  })
)

const logTimeLink = new ApolloLink((operation, forward) => {
  const localTime = getUnixTimestampFormat(new Date())
  return forward(operation).map(data => {
    const date = operation.getContext()?.response?.headers.get('Date')
    if (date) {
      timeDiff(getUnixTimestampFormat(date) - localTime)
    }
    return data
  })
})

const getMemoryUsageInfo = () => {
  if (performance.memory) {
    const memory = performance.memory
    const used = memory.usedJSHeapSize
    const total = memory.totalJSHeapSize
    const limit = memory.jsHeapSizeLimit

    const formatBytes = bytes => (bytes / 1024 / 1024).toFixed(2)

    const efficiencyPercent = ((used / total) * 100).toFixed(2)
    const stabilityPercent = ((used / limit) * 100).toFixed(2)

    return {
      efficiencyPercent,
      stabilityPercent,
      memoryUsedMb: formatBytes(used),
      memoryAllocatedMb: formatBytes(total),
      memoryLimitMb: formatBytes(limit),
      state: 'memory_api_enabled'
    }
  }

  return {
    state: 'memory_api_disabled'
  }
}

const getRequestLatencyInfo = targetDuration => {
  const timings = performance
    .getEntriesByType('resource')
    .filter(
      entry => entry.name === config.API_URL && entry.duration < targetDuration
    )

  performance.clearResourceTimings()

  if (timings.length === 0) return

  // Take an actual resource timing by finding the closest duration match
  // Using requestId query param approach negatively impacts performance
  const timing = timings.sort((a, b) => b.duration - a.duration)[0]

  // How long a request waits in the browser before being dispatched
  const clientLatency = timing.requestStart - timing.fetchStart

  // DNS lookup + TCP and TLS handshakes + Download time
  const networkLatency =
    timing.connectEnd -
    timing.domainLookupStart +
    (timing.responseEnd - timing.responseStart)

  // Time to First Byte
  const serverLatency = timing.responseStart - timing.requestStart

  const dictionaryLookup = mapMaxIndexToValue(['Client', 'Network', 'Server'])

  return {
    timing,
    timings,
    client: clientLatency,
    network: networkLatency,
    server: serverLatency,
    bottleneckSource: dictionaryLookup(
      clientLatency,
      networkLatency,
      serverLatency
    )
  }
}

const getTabsUsageInfo = () => {
  const activeTabId = getFromSessionStorage('activeTabId')()
  const openTabs = getFromSessionStorage('openTabs')() || []

  return {
    openTabs,
    activeTabId,
    openTabsAmount: openTabs.length + 1
  }
}

const SLOW_QUERY_THRESHOLD = 10000 // Track everything that takes more than 10 seconds
const monitoringLink = new ApolloLink((operation, forward) => {
  const operationAST = getOperationAST(operation.query, operation.operationName)

  if (operationAST?.operation === 'subscription') return forward(operation)

  const start = performance.now()

  return forward(operation).map(result => {
    try {
      const duration = performance.now() - start

      const datadogSessionInfo = window.DD_RUM?.getInternalContext?.()
      const isDatadogInitialized =
        datadogSessionInfo && datadogSessionInfo?.session_id

      if (isDatadogInitialized && duration > SLOW_QUERY_THRESHOLD) {
        datadogRum.addAction('Slow GraphQL Query', {
          operationName: operation.operationName,
          metrics: {
            duration,
            effectiveType: navigator.connection?.effectiveType,
            latency: getRequestLatencyInfo(duration),
            memory: getMemoryUsageInfo(),
            tabs: getTabsUsageInfo()
          }
        })
      }

      return result
    } catch (error) {
      logger.logError('monitoringLinkError', {}, error)
      return result
    }
  })
})

configureTicketedCable({ getToken })

// Exposed for legacy callers (auth.js `signOut`) that need to force-disconnect
// the current cable outside of Apollo's normal teardown path.
export const disconnectCable = disconnectTicketedCable

export const actionCableLink = ticketedActionCableLink

const hasSubscriptionOperation = ({ query: { definitions } }) =>
  definitions.some(
    ({ kind, operation }) =>
      kind === 'OperationDefinition' && operation === 'subscription'
  )

const hasUploadOperation = ({ query: { definitions } }) =>
  definitions.some(
    def => def.kind === 'OperationDefinition' && def.operation === 'mutation'
  )

// Cache init
export const cache = new InMemoryCache({
  possibleTypes: fragmentResolutions.possibleTypes,
  typePolicies
})

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.map(error =>
      handleGraphQLError(error, operation.getContext())
    )
  }

  if (networkError) {
    logger.logWarning('Network error', {}, networkError)
  }
})

const uploadLink = createUploadLink({ uri: config.API_URL })

const links = from([
  monitoringLink,
  errorLink,
  authLink,
  auxillaryHeadersLink,
  logTimeLink,
  split(
    hasSubscriptionOperation,
    actionCableLink,
    split(hasUploadOperation, uploadLink, httpLink)
  )
])

const client = new ApolloClient({
  link: links, // Ensure 'links' is defined elsewhere in your code
  resolvers: {
    DoctorProfileV2: {
      // Pre-defined value to distinguish provider profiles
      isProvider: () => true
    },
    FacilityProfileV2: {
      // Pre-defined value to distinguish facility profiles
      isFacility: () => true
    },
    Mutation: {
      aiProviderProfile: async (_, { npiId, name }) => {
        try {
          const response = await fetch(
            `${config.AI_BASE_URL}/provider/${npiId}/${encodeURIComponent(
              name
            )}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json'
              }
            }
          )

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          return await response.json()
        } catch (error) {
          throw new Error(
            `There was a problem with the fetch operation: ${error}`
          )
        }
      }
    }
  },
  cache,
  devtools: { enabled: true }
})

export default client
